import { downloadFile, requestJson, requestMultipart } from "@/lib/api";

export type VersionStatus = "DRAFT" | "APPROVED" | "SUPERSEDED";
export type VersionSource = "MANUAL" | "XLSX";

export interface VersionSummary {
  id: string;
  negotiationConditionId: string;
  scope: string;
  versionNumber: number;
  status: VersionStatus;
  source: VersionSource;
  note: string | null;
  ownEntryCount: number;
  inheritedEntryCount: number;
  approvedAt: string | null;
  approvedBy: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface EntryView {
  productId: string;
  productCode: number | null;
  productName: string;
  unitId: string;
  unitName: string;
  cutTypeId: string | null;
  cutName: string | null;
  price: number;
  origin: "OWN" | "INHERITED";
}

export interface VersionDetail {
  summary: VersionSummary;
  entries: EntryView[];
}

export interface OwnEntryInput {
  productId: string;
  unitId: string;
  cutTypeId: string | null;
  price: number;
}

export interface ImportPreviewRow {
  lineNumber: number;
  productCode: number | null;
  productName: string | null;
  unitLabel: string | null;
  cutName: string | null;
  referencePrice: number | null;  // PREÇO CADASTRADO (informativo)
  proposedPrice: number | null;    // NOVO PREÇO preenchido (null = "Mantido")
  salePrice: number | null;        // PREÇO DE VENDA do conjunto proposto (cálculo oficial, inclui piso)
  status: "ALTERADO" | "MANTIDO" | "ERRO" | "DUPLICADA";
  message: string;
  resolvedProductId: string | null;
  resolvedUnitId: string | null;
  resolvedCutTypeId: string | null;
}

export interface ImportPreviewResponse {
  fileName: string;
  totalRows: number;
  alteredCount: number;
  keptCount: number;
  errorCount: number;
  duplicateCount: number;
  baseChanged: boolean;
  confirmable: boolean;
  rows: ImportPreviewRow[];
}

// ---------------------------------------------------------------------------------------------------
// Simulação de rentabilidade da versão (16 colunas). Todos os valores vêm do backend — o FE não calcula
// custo/margem/piso; só ecoa o "novo preço" digitado e renderiza o que a resposta trouxer.
// ---------------------------------------------------------------------------------------------------

export interface SimOverride { productId: string; unitId: string; cutName: string | null; price: number; }

export interface ProfitabilityRow {
  productId: string; code: number | null; productName: string;
  presentationUnitId: string; presentationUnitName: string; cutName: string | null;
  salePrice: number | null; registeredPrice: number | null; priceFloorApplied: boolean;
  rawCost: number | null; rawCostUnitName: string | null; mermaSurcharge: number | null;
  operationalCost: number | null; packagingCost: number | null;
  costValue: number | null; costComplete: boolean;
  clientCommissionValue: number | null; intermediaryCommissionValue: number | null; taxesUnitValue: number | null;
  resultValue: number | null; marginPercent: number | null; marginStatus: string | null;
  partialResultValue: number | null; partialMarginPercent: number | null; resultLabel: string | null;
  conclusive: boolean; partial: boolean;
  costPartial: number | null; partialSubtotal: number | null; partialSubtotalLabel: string | null;
  reasons: string[];
}

export interface SimulationRow {
  productId: string; code: number | null; productName: string;
  unitId: string; unitName: string; cutName: string | null;
  current: ProfitabilityRow;
  simulated: ProfitabilityRow | null;   // null = linha não afetada pela simulação
  newPrice: number | null;              // eco do "novo preço" digitado; null = mantido
  priceOverridden: boolean;             // true = o usuário digitou um novo preço nesta linha
  changePercent: number | null;         // (venda simulada − venda atual)/venda atual × 100; null se inalterada
}

export interface ResolvedPercentValue { value: number | null; origin: string; explicitZero: boolean; }

export interface NegotiationSummaryDto {
  configured: boolean; message: string | null;
  clientCommissionPercent: ResolvedPercentValue | null;
  intermediaryCommissionPercent: ResolvedPercentValue | null;
  intermediaryCommissionBase: string | null; intermediaryIncompleteAfterClientCommission: boolean;
  targetMarginPercent: ResolvedPercentValue | null;
  minimumMarginPercent: ResolvedPercentValue | null;
  taxListOrigin: string | null; taxListState: string | null;
}

export interface SimulationResponse {
  conditionId: string; versionId: string; versionNumber: number; versionStatus: VersionStatus; source: VersionSource;
  customerName: string; unitId: string | null; unitName: string | null; negotiationScope: string;
  validFrom: string; validTo: string | null; referenceDate: string;
  financialVisible: boolean; editable: boolean;
  baseFingerprint: string; expectedVersion: number;
  negotiation: NegotiationSummaryDto;
  page: number; size: number; totalRows: number;
  rows: SimulationRow[];
}

const base = (conditionId: string) => `/api/v1/admin/negotiations/${conditionId}/price-table`;

export function fetchVersions(conditionId: string): Promise<VersionSummary[]> {
  return requestJson<VersionSummary[]>(`${base(conditionId)}/versions`, { method: "GET" });
}

export function fetchVersion(conditionId: string, versionId: string): Promise<VersionDetail> {
  return requestJson<VersionDetail>(`${base(conditionId)}/versions/${versionId}`, { method: "GET" });
}

export function createDraft(conditionId: string, source: VersionSource = "MANUAL"): Promise<VersionDetail> {
  return requestJson<VersionDetail>(`${base(conditionId)}/versions`, { method: "POST", body: { source } });
}

export function replaceEntries(
  conditionId: string,
  versionId: string,
  entries: OwnEntryInput[],
  note: string | null,
  expectedVersion: number | null,
): Promise<VersionDetail> {
  return requestJson<VersionDetail>(`${base(conditionId)}/versions/${versionId}/entries`, {
    method: "PUT",
    body: { entries, note, expectedVersion },
  });
}

export function approveVersion(
  conditionId: string,
  versionId: string,
  expectedVersion: number | null,
): Promise<VersionDetail> {
  return requestJson<VersionDetail>(`${base(conditionId)}/versions/${versionId}/approve`, {
    method: "POST",
    body: { expectedVersion },
  });
}

export function reviseVersion(conditionId: string, versionId: string): Promise<VersionDetail> {
  return requestJson<VersionDetail>(`${base(conditionId)}/versions/${versionId}/revise`, { method: "POST" });
}

export function discardDraft(conditionId: string, versionId: string): Promise<void> {
  return requestJson<void>(`${base(conditionId)}/versions/${versionId}`, { method: "DELETE" });
}

export function downloadTemplate(conditionId: string): Promise<void> {
  return downloadFile(`${base(conditionId)}/template.xlsx`, "modelo-tabela-negociacao.xlsx");
}

export function previewImport(
  conditionId: string,
  versionId: string,
  file: File,
): Promise<ImportPreviewResponse> {
  const form = new FormData();
  form.append("file", file);
  return requestMultipart<ImportPreviewResponse>(`${base(conditionId)}/versions/${versionId}/import/preview`, form);
}

export function confirmImport(
  conditionId: string,
  versionId: string,
  file: File,
  expectedVersion: number | null,
): Promise<VersionDetail> {
  const form = new FormData();
  form.append("file", file);
  if (expectedVersion != null) form.append("expectedVersion", String(expectedVersion));
  return requestMultipart<VersionDetail>(`${base(conditionId)}/versions/${versionId}/import/confirm`, form);
}

/** Prévia enriquecida (16 colunas): status por linha do arquivo + tabela simulada recalculada no backend. */
export interface ImportPreviewEnrichedResponse {
  fileName: string; totalRows: number;
  alteredCount: number; keptCount: number; errorCount: number; duplicateCount: number;
  baseChanged: boolean; confirmable: boolean;
  fileRows: ImportPreviewRow[];      // status/erros por linha do arquivo
  simulation: SimulationResponse;    // tabela de 16 colunas recalculada com os preços do arquivo
}

/**
 * Baixa o modelo XLSX de 16 colunas DESTA versão (coluna "Novo preço" vazia; demais informativas; colunas
 * financeiras só se o usuário tiver permissão de rentabilidade — o backend decide). Diferente do modelo
 * antigo (`downloadTemplate`) que é só por conditionId.
 */
export function downloadSimulationTemplate(conditionId: string, versionId: string): Promise<void> {
  return downloadFile(`${base(conditionId)}/versions/${versionId}/simulation/template.xlsx`, "tabela-negociacao-16col.xlsx");
}

/**
 * Prévia da importação por mesclagem com a tabela de 16 colunas recalculada pelo backend. Não aplica nada
 * — a aplicação continua em `confirmImport`.
 */
export function previewImportEnriched(
  conditionId: string,
  versionId: string,
  file: File,
): Promise<ImportPreviewEnrichedResponse> {
  const form = new FormData();
  form.append("file", file);
  return requestMultipart<ImportPreviewEnrichedResponse>(`${base(conditionId)}/versions/${versionId}/simulation/import/preview`, form);
}

/**
 * Simula a rentabilidade da versão aplicando os "novos preços" digitados (overrides). NÃO altera a versão
 * — devolve as linhas com o estado atual + o estado simulado para comparação. O piso/margem/custo vêm do
 * backend; o FE só ecoa os preços digitados.
 */
export function simulateVersion(
  conditionId: string,
  versionId: string,
  body: { overrides: SimOverride[]; query?: string | null; page?: number | null; size?: number | null },
): Promise<SimulationResponse> {
  return requestJson<SimulationResponse>(`${base(conditionId)}/versions/${versionId}/simulation`, { method: "POST", body });
}

/**
 * Aplica a simulação ao rascunho (persiste os overrides como preços próprios da versão). Exige
 * expectedVersion + baseFingerprint da última resposta de simulação; 409 quando a base/versão mudou.
 */
export function applySimulation(
  conditionId: string,
  versionId: string,
  body: {
    overrides: SimOverride[];
    expectedVersion: number | null;
    baseFingerprint: string | null;
    query?: string | null;
    page?: number | null;
    size?: number | null;
  },
): Promise<SimulationResponse> {
  return requestJson<SimulationResponse>(`${base(conditionId)}/versions/${versionId}/simulation/apply`, { method: "POST", body });
}
